/**
 * Mindmap View Module - Orchestrator
 *
 * Pure view: receives state, emits events via callbacks.
 * Traditional mindmap layout with curved connections.
 *
 * This file coordinates the sub-modules:
 * - state.js: Centralized state container
 * - layout.js: Tree layout calculations
 * - render.js: DOM rendering, node creation
 * - interaction.js: Mouse/touch/keyboard on nodes
 * - zoom-pan.js: Zoom and pan controls
 * - drag-drop.js: Drag and drop logic
 */

import * as Store from "../../store/index.js";
import * as MmCanvas from "./canvas-renderer.js";
import { isMobile, isMobileShellVisible } from "../../../../shared/utils/device-detect.js";
import { centeringOffset } from '../../ui/view-centering.js';
import { viewLabel } from '../../../../engine/core/view-names.js';

// Import sub-modules
import * as State from './state.js';
import * as Layout from './layout.js';
import * as Render from './render.js';
import * as Interaction from './interaction.js';
import * as ZoomPan from './zoom-pan.js';
import * as DragDrop from './drag-drop.js';

/**
 * Warn once if CRDT batch callbacks are missing (helps detect wiring issues in production)
 */
function warnMissingBatchCallbacks() {
  if (!State.warnedMissingCallbacks && (!State.callbacks?.beginBatch || !State.callbacks?.endBatch)) {
    console.warn("[Mindmap] CRDT batch callbacks (beginBatch/endBatch) not provided - batching disabled. Check view wiring in main.js sharedCallbacks.");
    State.setWarnedMissingCallbacks(true);
  }
}

/**
 * Render the Mindmap view
 * @param {HTMLElement} containerEl - Container element
 * @param {Object} state - { data, focusId, selectedId, hideCompleted }
 * @param {Object} cbs - Callbacks for actions
 */
export function render(containerEl, state, cbs) {
  State.setContainer(containerEl);
  State.setCallbacks(cbs);
  State.setCurrentState(state);

  // Use existing HTML structure (don't rebuild it)
  State.setWorld(containerEl.querySelector("#m-world"));
  State.setSvg(containerEl.querySelector("#m-svg"));
  State.setCanvas(containerEl.querySelector("#m-canvas"));

  if (!State.world || !State.svg || !State.canvas) {
    console.error("Mindmap view: Required elements not found");
    return;
  }

  // Initialize sub-modules with callbacks
  const layoutCallbacks = {
    triggerRender: Render.renderMindmap,
  };
  Layout.init(layoutCallbacks);

  const dragCallbacks = {
    triggerRender: Render.renderMindmap,
    updateTransform: ZoomPan.updateTransform,
  };
  DragDrop.init(dragCallbacks);

  const interactionCallbacks = {
    onNodeSelect: cbs.onNodeSelect,
    onNodeEdit: cbs.onNodeEdit,
    onNodeContextMenu: cbs.onNodeContextMenu,
    onOpenModal: cbs.onOpenModal,
    onNavigate: cbs.onNavigate,
    isReadOnly: cbs.isReadOnly,
    zoomTo: ZoomPan.zoomTo,
  };
  Interaction.init(interactionCallbacks);

  const renderCallbacks = {
    ...cbs,
    setupDrag: DragDrop.setupMmDrag,
    zoomTo: ZoomPan.zoomTo,
    updateTransform: ZoomPan.updateTransform,
  };
  Render.init(renderCallbacks);

  const zoomCallbacks = {
    onNavigate: cbs.onNavigate,
    triggerRender: Render.renderMindmap,
    increaseGap: Layout.increaseGap,
    decreaseGap: Layout.decreaseGap,
    onCanvasContextMenu: cbs.onCanvasContextMenu,
  };
  ZoomPan.init(zoomCallbacks);

  // Only attach event listeners once to prevent duplicates
  if (!State.listenersAttached) {
    ZoomPan.setupZoomControls();
    ZoomPan.setupCanvasInteraction();
    State.setListenersAttached(true);
  }

  // Always restore transform from Store when render is called (map may have changed)
  // Heal polluted persists: leaked mobile preview transforms (fixed at the
  // source now, but existing profiles carry 0.1/0.02 values from the mobile
  // fit clamps) restored the desktop at 2-10% zoom — a microscopic map in a
  // blank canvas. Threshold 0.15: desktop CAN reach lower via zoom-out
  // clicks, but anything under 15% is an unusable thumbnail nobody parks at,
  // so resetting those to the centered default is the better failure mode.
  // Mobile-shell exception: a preview-owned live transform (_mobilePreview,
  // stamped by setDesktopViewTransform for pinch/pan/fit) survives full
  // renders — mobile previews never write the Store transform, so restoring
  // here snapped the user's view back on every remote edit / shell remount.
  if (!(isMobileShellVisible() && State.mState?._mobilePreview)) {
    const savedTransform = Store.get("mindmapTransform");
    if (savedTransform && savedTransform.zoom >= 0.15) {
      State.setMState(savedTransform);
      State.setLastSavedTransform(savedTransform);
    } else {
      State.setMState({ zoom: 1, panX: 0, panY: 0 });
      State.setLastSavedTransform({ zoom: 1, panX: 0, panY: 0 });
    }
  }
  ZoomPan.updateTransform();

  // Restore gap from Store
  const savedGap = Store.get("mindmapGap");
  if (savedGap !== undefined && savedGap !== null) {
    State.setGapPercent(savedGap);
  } else {
    State.setGapPercent(Layout.DEFAULT_GAP_PERCENT);
  }
  Layout.updateGapDisplay();

  // Level-0 layout mode (balanced / right / left) — per-doc local preference.
  State.setLayoutMode(Store.get("mindmapLayoutMode") || "balanced");

  // Canvas mode (mobile): paint the map to a single canvas instead of
  // building per-node DOM. Mount is re-entrant — render() may be called on a
  // container whose previous canvas was orphaned by an innerHTML rebuild.
  if (canvasModeEnabled()) {
    if (MmCanvas.isMounted()) MmCanvas.unmount();
    MmCanvas.mount({
      host: State.canvas,
      getTransform: () => State.mState,
      getSelectedId: () => Store.get("selectedId"),
      onTap: (nodeId) => cbs.onNodeSelect?.(nodeId),
      onLongPress: (nodeId) => cbs.onNodeLongPress?.(nodeId),
    });
  } else if (MmCanvas.isMounted()) {
    MmCanvas.unmount();
  }

  Render.renderMindmap();
}

// Canvas renderer: default ON for mobile (a ~700-node mindmap costs ~1s of
// DOM/SVG construction per render on a phone; the canvas paints the same
// scene in a few ms). Opt out with localStorage.orbital_canvas_mindmap='0'.
// Desktop keeps the DOM renderer (drag, inline rename, context menus).
function canvasModeEnabled() {
  try {
    return isMobile() && localStorage.getItem("orbital_canvas_mindmap") !== "0";
  } catch {
    return false;
  }
}

/**
 * World-space bounds of the painted scene — used by the mobile fit-to-view
 * in canvas mode, where no DOM nodes exist to measure.
 */
export function getContentBounds() {
  return MmCanvas.getContentBounds();
}

/** Pan to a rendered task without changing zoom or node layout. */
export function centerOnNode(nodeId) {
  const task = State.world?.querySelector(`.mm-node[data-id="${CSS.escape(nodeId)}"]`);
  if (!task) return false;
  // Finish any previous pan before measuring; focus can otherwise scroll the
  // canvas while the task is still animating toward its destination.
  const transition = State.world.style.transition;
  State.world.style.transition = 'none';
  try {
    const offset = centeringOffset(State.canvas, task);
    if (!offset) return false;
    State.setMState({panX: State.mState.panX + offset.x, panY: State.mState.panY + offset.y});
    ZoomPan.updateTransform();
    State.world.getBoundingClientRect();
    return true;
  } finally { State.world.style.transition = transition; }
}

export function refreshRoutineNodes() {
  if(!State.container||!State.callbacks)return;
  Render.renderMindmap();
}

/**
 * Update the view with new state. The layout is recomputed from scratch on
 * every render (see tidy-layout.js), so structural changes need no special
 * handling here.
 */
export function update(prevState, nextState) {
  State.setCurrentState(nextState);

  if (!State.container || !State.callbacks) return;

  // Skip the full DOM rebuild when nothing the mindmap renders has changed.
  // The mindmap draws no selection indicator, and both mindmapTransform
  // writers (zoom-pan drag/wheel, main.js view-restore) apply the transform
  // to the view DIRECTLY before committing to the Store — so a store update
  // that only touches those keys re-creates byte-identical DOM. Every other
  // key (data, focusId, tag settings, …) falls through to a full render.
  if (prevState && _onlyIgnorableKeysChanged(prevState, nextState)) {
    return;
  }

  Render.renderMindmap();
}

// Store-state keys whose changes don't alter mindmap output (see update()).
const RENDER_IGNORED_KEYS = new Set(["selectedId", "mindmapTransform"]);

/**
 * True when every differing key between the two states is render-ignorable.
 * Strict-equality per key: `data` is a fresh object after any CRDT change,
 * so content edits always fail the check and trigger a full render.
 */
function _onlyIgnorableKeysChanged(prevState, nextState) {
  const keys = new Set([...Object.keys(prevState), ...Object.keys(nextState)]);
  for (const key of keys) {
    if (prevState[key] !== nextState[key] && !RENDER_IGNORED_KEYS.has(key)) {
      return false;
    }
  }
  return true;
}

/**
 * Clean up the view
 * MD8 Fix: Ensures all module-level state is properly reset
 */
export function destroy() {
  if (MmCanvas.isMounted()) MmCanvas.unmount();
  State.resetState();
}

/**
 * Zoom towards mouse position (for keyboard shortcuts)
 */
export function zoomTowardsMouse(zoomDelta, mouseX, mouseY) {
  ZoomPan.zoomTowardsMouse(zoomDelta, mouseX, mouseY);
}

// NOTE: resetLayout() was removed (audit, 2026-06-12). It had zero callers —
// the "R = reset layout" keyboard binding it was written for no longer
// exists (R toggles the Routines panel; see keyboard-manager.js). The shortcuts help
// modal entry was removed at the same time.

// Export view info
export const viewInfo = {
  name: "mindmap",
  get label() { return viewLabel("mindmap"); },
  icon: "project-diagram",
};
